import { UpdateDevicesSuccessAction } from './../actions/devices';
import { App, AppDevice, AppEntity, Organization, ProvisioningProfile } from '../types';
import { LOGOUT_SUCCESS, LogoutSuccessAction } from '../actions/login';
import { difference, flatMap, forEach, keyBy, map, uniq, unset } from 'lodash';
import { combineReducers } from 'redux';
import { UPDATE_APP_SETTINGS_SUCCESS, UpdateAppSettingsSuccessAction } from '../actions/appSettings';
import {
    CREATE_INTEGRATION_REQUEST_SUCCESS,
    CreateIntegrationSuccessAction,
    DELETE_INTEGRATION_REQUEST_SUCCESS,
    DeleteIntegrationSuccessAction,
    REFRESH_INTEGRATION_REQUEST_SUCCESS,
    RefreshIntegrationSuccessAction,
    UPDATE_INTEGRATION_REQUEST_SUCCESS,
    UPDATE_INTEGRATIONS_REQUEST_SUCCESS,
    UpdateIntegrationsSuccessAction,
    UpdateIntegrationSuccessAction,
} from '../actions/integrations';
import {
    APP_ENTITY_UPDATED,
    APP_STATE_UPDATED,
    AppEntityUpdatedAction,
    APPS_ADDED_SUCCESS,
    APPS_DELETED,
    APPS_UPDATED,
    AppsAddedSuccessAction,
    AppsDeletedAction,
    AppStateUpdatedAction,
    AppsUpdatedAction,
    ConnectedAppEntityResponse,
    DeletedAppEntityPayload,
    FETCH_APP_STATE_SUCCESS,
    FetchAppStateSuccessAction,
} from '../actions/app';
import { FETCH_APP_HOME_SUCCESS, FetchAppHomeSuccessAction } from '../actions/appHome';
import { CLEAR_STATE, ClearStateAction, FETCH_STATE_SUCCESS, FetchStateSuccessAction } from '../actions/state';
import { FETCH_ORG_OVERVIEW_V2_SUCCESS, FetchOrgOverviewV2SuccessAction } from '../actions/orgOverview';
import { AppEntityRequest } from '../actions/entities';
import { UPDATE_DEVICES_REQUEST_SUCCESS } from '../actions/devices';
import {
    DELETE_CHECKLIST_ITEM_TEMPLATES,
    DeleteChecklistItemTemplatesAction,
    UpdateChecklistItemTemplatesAction,
    UPDATE_CHECKLIST_ITEM_TEMPLATES,
} from '@/actions/checklistItems';
import { UPDATE_AUTOMATIONS, UpdateAutomationsAction } from '@/actions/automations';
import {
    CLEAR_WORKFLOW_OVERVIEW,
    ClearWorkflowOverviewAction,
    UPDATE_WORKFLOW_OVERVIEW,
    UPDATE_WORKFLOW_STEP_GRAPHS,
    UpdateWorkflowOverviewAction,
    UpdateWorkflowStepGraphAction,
} from '@/actions/workflow';

export const FETCH_ALL_DEVICES_SUCCESS = 'FETCH_ALL_DEVICES_SUCCESS';
export const FETCH_PROVISIONING_PROFILES_SUCCESS = 'FETCH_PROVISIONING_PROFILES_SUCCESS';

export interface FetchProvisioningProfilesResponsePayload extends AppEntityRequest {
    provisioningProfiles: ProvisioningProfile[];
}

export interface FetchProvisioningProfilesSuccessAction {
    type: typeof FETCH_PROVISIONING_PROFILES_SUCCESS;
    payload: FetchProvisioningProfilesResponsePayload;
}

export const fetchProvisioningProfilesSuccess: (
    payload: FetchProvisioningProfilesResponsePayload,
) => FetchProvisioningProfilesSuccessAction = (payload) => {
    return {
        type: FETCH_PROVISIONING_PROFILES_SUCCESS,
        payload,
    };
};

export interface FetchAllDevicesResponsePayload extends AppEntityRequest {
    devices: AppDevice[];
    devicesRegisteredInASCCount: number;
}

export interface FetchAllDevicesSuccessAction {
    type: typeof FETCH_ALL_DEVICES_SUCCESS;
    payload: FetchAllDevicesResponsePayload;
}

export const fetchAllDevicesSuccess: (payload: FetchAllDevicesResponsePayload) => FetchAllDevicesSuccessAction = (
    payload,
) => {
    return {
        type: FETCH_ALL_DEVICES_SUCCESS,
        payload,
    };
};

interface AppsById {
    [id: string]: AppEntity;
}

type AppsActionTypes =
    | FetchAppStateSuccessAction
    | UpdateDevicesSuccessAction
    | FetchAllDevicesSuccessAction
    | FetchStateSuccessAction
    | UpdateAppSettingsSuccessAction
    | CreateIntegrationSuccessAction
    | RefreshIntegrationSuccessAction
    | UpdateIntegrationSuccessAction
    | UpdateIntegrationsSuccessAction
    | DeleteIntegrationSuccessAction
    | AppStateUpdatedAction
    | LogoutSuccessAction
    | FetchAppHomeSuccessAction
    | AppsAddedSuccessAction
    | AppsUpdatedAction
    | AppsDeletedAction
    | FetchProvisioningProfilesSuccessAction
    | FetchOrgOverviewV2SuccessAction
    | ClearStateAction
    | UpdateAutomationsAction
    | UpdateChecklistItemTemplatesAction
    | DeleteChecklistItemTemplatesAction
    | UpdateWorkflowOverviewAction
    | ClearWorkflowOverviewAction
    | UpdateWorkflowStepGraphAction
    | AppEntityUpdatedAction;

const appEntitiesFromApps: (apps: App[]) => AppEntity[] = (apps) => {
    return apps.map((app) => {
        const {
            id,
            appId,
            appSettings,
            releaseStepArchetypes,
            users,
            stabilityHistory,
            totalAutomationsCount,
            totalTimeSavedFromAutomations,
            totalCostSavingFromAutomations,
            createdAt,
            isRestrictedApp,
            completedReleaseTotalCount,
            buildBuckets,
            devices,
            provisioningProfiles,
            liveReleaseId,
            workflowId,
        } = app;

        return {
            id,
            appId,
            appSettings,
            releaseStepArchetypes,
            users,
            appHome: null,
            createdAt,
            stabilityHistory: stabilityHistory ? { ...stabilityHistory } : null,
            totalAutomationsCount: totalAutomationsCount ? { ...totalAutomationsCount } : null,
            totalTimeSavedFromAutomations: totalTimeSavedFromAutomations ? { ...totalTimeSavedFromAutomations } : null,
            totalCostSavingFromAutomations:
                totalCostSavingFromAutomations ? { ...totalCostSavingFromAutomations } : null,
            provisioningProfiles: provisioningProfiles ?? [],
            isRestrictedApp,
            completedReleaseTotalCount,
            buildBuckets,
            devices,
            liveReleaseId,
            workflowId,
        };
    });
};

function createAppsById(state: AppsById, action: FetchStateSuccessAction): AppsById {
    const allAppEntities = flatMap(action.payload.organizations, (org) => {
        return appEntitiesFromApps(org.apps as App[]);
    });

    const appsById = keyBy(allAppEntities, (app) => app.id);

    return {
        ...state,
        ...appsById,
    };
}

function addOrUpdateApps(state: AppsById, payload: ConnectedAppEntityResponse[]): AppsById {
    const appEntities = payload.map((connectedAppEntity) => connectedAppEntity.app);

    const appsById = keyBy(appEntities, (app) => app.id);

    return {
        ...state,
        ...Object.entries(appsById).reduce((acc, [appId, app]) => {
            const existingApp = state[appId];
            if (existingApp) {
                acc[appId] = {
                    ...existingApp,
                    ...app,
                };
            } else {
                acc[appId] = app;
            }

            return acc;
        }, {}),
    };
}

function updateAppById(state: AppsById, app: AppEntity): AppsById {
    const appId = app.id;
    const existingApp = state[appId];

    // This seems like tech debt to me... hopefully we can remove when we migrate app to workflow
    return {
        ...state,
        [appId]: {
            id: app.id,
            appId: app.appId,
            releaseStepArchetypes: app.releaseStepArchetypes,
            appSettings: {
                ...app.appSettings,
            },
            users: [...app.users],
            appHome:
                existingApp?.appHome && app.appHome ? { ...existingApp.appHome, ...app.appHome }
                : existingApp?.appHome ? { ...existingApp.appHome }
                : app.appHome ? { ...app.appHome }
                : null,
            createdAt: app.createdAt,
            stabilityHistory: app.stabilityHistory ? { ...app.stabilityHistory } : null,
            totalAutomationsCount: app.totalAutomationsCount ? { ...app.totalAutomationsCount } : null,
            totalTimeSavedFromAutomations:
                app.totalTimeSavedFromAutomations ? { ...app.totalTimeSavedFromAutomations } : null,
            totalCostSavingFromAutomations:
                app.totalCostSavingFromAutomations ? { ...app.totalCostSavingFromAutomations } : null,
            provisioningProfiles: app.provisioningProfiles ?? [],
            isRestrictedApp: app.isRestrictedApp,
            completedReleaseTotalCount: app.completedReleaseTotalCount,
            buildBuckets: app.buildBuckets ?? [],
            devices: existingApp?.devices ?? app?.devices ?? [],
            liveReleaseId: app.liveReleaseId,
            workflowId: app.workflowId,
        },
    };
}

function deleteApps(state, apps: DeletedAppEntityPayload[]): AppsById {
    const updatedState = {
        ...state,
    };

    forEach(apps, (deleteAppEntity) => {
        unset(updatedState, deleteAppEntity.appId);
    });

    return updatedState;
}

function allAppIdsInOrgs(orgs: Organization[]) {
    return flatMap(orgs, (org) => {
        return map(org.apps, (app) => app.id);
    });
}

function updateAppSettingsReducer(
    state: AppsById = {},
    action: UpdateAppSettingsSuccessAction | UpdateIntegrationSuccessAction,
): AppsById {
    const app = state[action.payload.appId];

    if (app) {
        return {
            ...state,
            [app.id]: {
                ...app,
                appSettings: {
                    ...action.payload.appSettings,
                },
            },
        };
    } else {
        return state;
    }
}

function partiallyUpdateAppSettingsWithStepGraphReducer(
    state: AppsById = {},
    action: UpdateWorkflowStepGraphAction,
): AppsById {
    const newState = { ...state };
    for (const workflowStepGraph of action.payload) {
        const app = newState[workflowStepGraph.legacyAppId];
        if (app) {
            newState[workflowStepGraph.legacyAppId] = {
                ...app,
                appSettings: {
                    ...app.appSettings,
                    stepGraph: { ...app.appSettings.stepGraph, stepGroups: workflowStepGraph.stepGroups },
                },
            };
        }
    }
    return newState;
}

function partiallyUpdateAppSettingsWithIntegrationsReducer(
    state: AppsById = {},
    action: UpdateIntegrationsSuccessAction,
): AppsById {
    const app = state[action.payload.appId];

    if (app) {
        return {
            ...state,
            [app.id]: {
                ...app,
                appSettings: {
                    ...app.appSettings,
                    integrations: action.payload.integrations,
                },
            },
        };
    } else {
        return state;
    }
}

function updateAppHomeReducer(state: AppsById = {}, action: FetchAppHomeSuccessAction): AppsById {
    const app = state[action.payload.appId];

    if (app) {
        return {
            ...state,
            [app.id]: {
                ...app,
                appHome: {
                    ...action.payload.appHome,
                },
            },
        };
    } else {
        return state;
    }
}

function updateWorkflowOverviewReducer(state: AppsById = {}, action: UpdateWorkflowOverviewAction): AppsById {
    const app = state[action.payload.appId];
    if (app) {
        return {
            ...state,
            [app.id]: { ...app, workflowOverview: action.payload },
        };
    }

    return state;
}

function clearWorkflowOverviewReducer(state: AppsById = {}, action: ClearWorkflowOverviewAction): AppsById {
    const app = state[action.payload.appId];
    if (app) {
        return {
            ...state,
            [app.id]: { ...app, workflowOverview: null },
        };
    }

    return state;
}

function partiallyUpdateAppHomeReducer(state: AppsById = {}, action: FetchOrgOverviewV2SuccessAction): AppsById {
    if (action.payload.liveRelease || action.payload.nextRelease) {
        const app = state[action.payload.singleAppId];
        if (app) {
            return {
                ...state,
                [app.id]: {
                    ...app,
                    appHome: {
                        ...app.appHome,
                        liveRelease: app.appHome?.liveRelease ?? action.payload.liveRelease,
                        nextRelease: app.appHome?.nextRelease ?? action.payload.nextRelease,
                    },
                },
            };
        }
    }

    return state;
}

function partiallyUpdateAppDevices(
    state: AppsById = {},
    action: FetchAllDevicesSuccessAction | UpdateDevicesSuccessAction,
): AppsById {
    const appId = action.payload.appId;
    if (appId) {
        const app = state[appId];
        if (app) {
            return {
                ...state,
                [app.id]: {
                    ...app,
                    devices: action.payload.devices ?? app.devices ?? [],
                    devicesRegisteredInASCCount:
                        action.payload.devicesRegisteredInASCCount ?? app.devicesRegisteredInASCCount ?? 0,
                },
            };
        }
    }

    return state;
}

function partiallyUpdateAppProvisioningProfiles(
    state: AppsById = {},
    action: FetchProvisioningProfilesSuccessAction,
): AppsById {
    const appId = action.payload.appId;
    if (appId) {
        const app = state[appId];
        if (app) {
            return {
                ...state,
                [app.id]: {
                    ...app,
                    provisioningProfiles: action.payload.provisioningProfiles ?? app.provisioningProfiles ?? [],
                },
            };
        }
    }

    return state;
}

// Main reducer functions
function appsById(state: AppsById = {}, action: AppsActionTypes): AppsById {
    switch (action.type) {
        case FETCH_STATE_SUCCESS:
            return createAppsById(state, action);
        case DELETE_INTEGRATION_REQUEST_SUCCESS:
        case CREATE_INTEGRATION_REQUEST_SUCCESS:
        case REFRESH_INTEGRATION_REQUEST_SUCCESS:
            return updateAppById(state, action.payload.appEntity);
        case UPDATE_APP_SETTINGS_SUCCESS:
        case UPDATE_INTEGRATION_REQUEST_SUCCESS:
            return updateAppSettingsReducer(state, action);
        case UPDATE_INTEGRATIONS_REQUEST_SUCCESS:
            return partiallyUpdateAppSettingsWithIntegrationsReducer(state, action);
        case FETCH_APP_STATE_SUCCESS:
            return updateAppById(state, action.payload.app);
        case APP_STATE_UPDATED:
            return updateAppById(state, action.payload.app);
        case APP_ENTITY_UPDATED:
            return updateAppById(state, action.payload);
        case FETCH_APP_HOME_SUCCESS:
            return updateAppHomeReducer(state, action);
        case UPDATE_WORKFLOW_OVERVIEW:
            return updateWorkflowOverviewReducer(state, action);
        case CLEAR_WORKFLOW_OVERVIEW:
            return clearWorkflowOverviewReducer(state, action);
        case UPDATE_WORKFLOW_STEP_GRAPHS:
            return partiallyUpdateAppSettingsWithStepGraphReducer(state, action);
        case FETCH_ORG_OVERVIEW_V2_SUCCESS:
            return partiallyUpdateAppHomeReducer(state, action);
        case FETCH_PROVISIONING_PROFILES_SUCCESS:
            return partiallyUpdateAppProvisioningProfiles(state, action);
        case FETCH_ALL_DEVICES_SUCCESS:
        case UPDATE_DEVICES_REQUEST_SUCCESS:
            return partiallyUpdateAppDevices(state, action);
        case APPS_UPDATED:
        case APPS_ADDED_SUCCESS:
            return addOrUpdateApps(state, action.payload);
        case APPS_DELETED:
            return deleteApps(state, action.payload);
        case UPDATE_AUTOMATIONS: {
            // TODO: #WORKFLOWS# move this to workflow reducer whenever we migrate app to workflow
            const newState = { ...state };
            for (const automation of action.payload) {
                const app = newState[automation.legacyAppId];
                if (app) {
                    const originalAutomations = [...(app.appSettings.automations ?? [])];

                    const updatedAutomations = originalAutomations.map((existingAutomation) => {
                        if (existingAutomation.id === automation.id) {
                            return {
                                ...existingAutomation,
                                ...automation,
                            };
                        }
                        return existingAutomation;
                    });

                    newState[automation.legacyAppId] = {
                        ...app,
                        appSettings: {
                            ...app.appSettings,
                            automations: updatedAutomations,
                        },
                    };
                }
            }
            return newState;
        }
        case UPDATE_CHECKLIST_ITEM_TEMPLATES: {
            // TODO: #WORKFLOWS# move this to workflow reducer whenever we migrate app to workflow
            const newState = { ...state };
            for (const checklistItem of action.payload) {
                const app = newState[checklistItem.legacyAppId];
                if (app) {
                    const originalStepChecklistItems = [...(app.appSettings.stepChecklistItems ?? [])];

                    let notFound = true;
                    const updatedStepChecklistItems = originalStepChecklistItems.map((stepChecklistItem) => {
                        if (stepChecklistItem.id === checklistItem.id) {
                            notFound = false;
                            return {
                                ...stepChecklistItem,
                                ...checklistItem,
                            };
                        }
                        return stepChecklistItem;
                    });

                    if (notFound) {
                        updatedStepChecklistItems.push(checklistItem);
                    }

                    newState[checklistItem.legacyAppId] = {
                        ...app,
                        appSettings: {
                            ...app.appSettings,
                            stepChecklistItems: updatedStepChecklistItems,
                        },
                    };
                }
            }
            return newState;
        }
        case DELETE_CHECKLIST_ITEM_TEMPLATES: {
            const updatedState = { ...state };
            for (const appId in updatedState) {
                const app = updatedState[appId];
                if (app) {
                    const originalStepChecklistItems = [...(app.appSettings.stepChecklistItems ?? [])];

                    const updatedStepChecklistItems = originalStepChecklistItems.filter(
                        (stepChecklistItem) => !action.payload.includes(stepChecklistItem.id),
                    );

                    updatedState[appId] = {
                        ...app,
                        appSettings: {
                            ...app.appSettings,
                            stepChecklistItems: updatedStepChecklistItems,
                        },
                    };
                }
            }
            return updatedState;
        }
        case LOGOUT_SUCCESS:
        case CLEAR_STATE:
            return {};
        default:
            return state;
    }
}

function allIds(state: string[] = [], action: AppsActionTypes) {
    switch (action.type) {
        case FETCH_STATE_SUCCESS:
            return allAppIdsInOrgs(action.payload.organizations);
        case APP_STATE_UPDATED:
            state.push(action.payload.app.id);

            return uniq(state);
        case APPS_UPDATED:
        case APPS_ADDED_SUCCESS: {
            const addedAppIds = action.payload.map((data) => data.app.id);
            const newAppIds = state.concat(addedAppIds);

            return uniq(newAppIds);
        }
        case APPS_DELETED: {
            const deletedAppIds = action.payload.map((app) => app.appId);
            return difference(state, deletedAppIds);
        }
        case LOGOUT_SUCCESS:
        case CLEAR_STATE:
            return [];
        default:
            return state;
    }
}

const appsReducer = combineReducers({
    byId: appsById,
    allIds: allIds,
});

export default appsReducer;
